import { Shimmer, ShimmerElementType } from '@fluentui/react';
import { ClassNamesFromIStyles, useClassNames } from '@h2oai/ui-kit';
import React from 'react';

import { IStatCardStyles, statCardStylesDefault } from './StatCard.styles';

interface SubStat {
  label: string;
  value: number | string;
}

interface StatCardProps {
  title?: string;
  mainLabel: string;
  mainValue: number | string;
  subStats: SubStat[];
  statsLoading: boolean;
  statsError: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({
  title = 'Stats',
  mainLabel,
  mainValue,
  subStats,
  statsLoading,
  statsError,
}) => {
  const classNames = useClassNames<IStatCardStyles, ClassNamesFromIStyles<IStatCardStyles>>(
    'gpte-overall-data',
    statCardStylesDefault
  );

  const formatValue = (val: number | string) => (typeof val === 'number' ? val.toLocaleString() : val);

  return (
    <div className={classNames.card}>
      {statsError && (
        <div className={classNames.errorOverlay}>
          <div className={classNames.errorContent}>
            <span className={classNames.overlayText}>An Error Occurred</span>
          </div>
        </div>
      )}

      {!mainValue && !statsLoading && (
        <div className={classNames.errorOverlay}>
          <div className={classNames.errorContent}>
            <span className={classNames.noDataText}>No Data</span>
          </div>
        </div>
      )}

      <div className={classNames.header}>
        <h3 className={classNames.title}>{title}</h3>
      </div>

      <div className={classNames.mainStat}>
        {statsLoading ? (
          <>
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 32, width: '60%' }]} />
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 16, width: '40%' }]} />
          </>
        ) : (
          <>
            <span className={classNames.mainNumber}>{formatValue(mainValue)}</span>
            <span className={classNames.mainLabel}>{mainLabel}</span>
          </>
        )}
      </div>

      <div className={classNames.subStats}>
        {statsLoading
          ? Array.from({ length: subStats.length || 3 }).map((_, i) => (
              <React.Fragment key={i}>
                <div className={classNames.subStat}>
                  <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 24, width: '70%' }]} />
                  <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 12, width: '50%' }]} />
                </div>
                {i < (subStats.length || 3) - 1 && <div className={classNames.divider} />}
              </React.Fragment>
            ))
          : subStats.map((stat, i) => (
              <React.Fragment key={i}>
                <div className={classNames.subStat}>
                  <span className={classNames.subNumber}>{formatValue(stat.value)}</span>
                  <span className={classNames.subLabel}>{stat.label}</span>
                </div>
                {i < subStats.length - 1 && <div className={classNames.divider} />}
              </React.Fragment>
            ))}
      </div>
    </div>
  );
};
